import React, { useState, useEffect, useMemo, useRef } from 'react'
import { X, MapPin, Calendar, Tag, FolderPlus, FileText, Copy, Plus, CheckCircle } from '@/ui/icons'
import { WaypointResponse } from '@/shared/api/routes'
import { cn } from '@/shared/utils'
import { Button, Input, Sheet, SheetContent, SheetBody } from '@/ui/components'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { AddWaypointToLibraryDialog } from '../dialogs'
import { AddWaypointToTripPanel } from './AddWaypointToTripPanel'
import { formatCoordinatesForCopy, copyCoordinatesToClipboard } from '@/modules/trips/utils/coordinateUtils'

type POIType = 'gas' | 'water' | 'viewpoint' | 'campsite' | 'meetup_spot' | 'trailhead' | 'custom' | null

/** Matches trip detail right panel close control (TripDetailRightPanel). */
const FLOATING_CLOSE_BUTTON_CLASS = 'min-h-[44px] min-w-[44px]'

const POI_TYPE_OPTIONS: Array<{ value: POIType; label: string }> = [
  { value: null, label: 'None' },
  { value: 'campsite', label: 'Campsite' },
  { value: 'meetup_spot', label: 'Meetup Spot' },
  { value: 'gas', label: 'Fuel' },
  { value: 'trailhead', label: 'Trailhead' },
  { value: 'viewpoint', label: 'Viewpoint' },
  { value: 'water', label: 'Water' },
  { value: 'custom', label: 'Custom' }
]

interface WaypointDetailsPanelProps {
  waypoint?: WaypointResponse | null
  coordinates?: { latitude: number; longitude: number; elevation?: number | null } | null
  /** Shown as heading when viewing coordinates only (e.g. explore coordinate search). */
  locationDisplayName?: string | null
  tripId?: string | null
  onClose: () => void
  onAddStop?: () => void
  isAddingStop?: boolean
  onUpdateWaypoint?: (waypointId: string, updates: { name?: string; poi_type?: string | null; poi_custom_label?: string | null; notes?: string }) => Promise<void>
  onUpdateWaypointDay?: (waypointId: string, day: number) => Promise<void>
  totalDays?: number
  className?: string
  isLibraryView?: boolean
  onAddToLibrary?: () => Promise<void>
  /**
   * `floating` opens as a bottom drawer (Sheet) on all viewports—same pattern as trip detail on mobile.
   * `dock` is the legacy full-height map sidebar (itinerary builder).
   */
  panelVariant?: 'dock' | 'floating'
}

export function WaypointDetailsPanel({
  waypoint,
  coordinates,
  locationDisplayName,
  tripId,
  onClose,
  onAddStop,
  isAddingStop = false,
  onUpdateWaypoint,
  onUpdateWaypointDay,
  totalDays,
  className,
  isLibraryView = false,
  onAddToLibrary,
  panelVariant = 'dock'
}: WaypointDetailsPanelProps) {
  const isFloating = panelVariant === 'floating'
  const [isEditingName, setIsEditingName] = useState(false)
  const [localName, setLocalName] = useState(waypoint?.name || '')
  const [isUpdatingName, setIsUpdatingName] = useState(false)
  const [isUpdatingDay, setIsUpdatingDay] = useState(false)
  const [isUpdatingType, setIsUpdatingType] = useState(false)
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false)
  const [localNotes, setLocalNotes] = useState(waypoint?.notes || '')
  const [customLabel, setCustomLabel] = useState(waypoint?.poi_custom_label || '')
  const [showAddToLibraryDialog, setShowAddToLibraryDialog] = useState(false)
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false)
  const [showAddToTripPanel, setShowAddToTripPanel] = useState(false)
  const [copiedCoordinates, setCopiedCoordinates] = useState(false)
  const pendingNameUpdateRef = useRef<{ value: string } | null>(null)
  const pendingNotesUpdateRef = useRef<{ value: string } | null>(null)
  const pendingLabelUpdateRef = useRef<{ value: string } | null>(null)
  
  // Check if waypoint is trip-specific (not in library)
  const isTripSpecificWaypoint = waypoint && (waypoint.folder_id === null || waypoint.folder_id === undefined)
  // Check if waypoint is in library (has folder_id)
  const isLibraryWaypoint = waypoint && waypoint.folder_id !== null && waypoint.folder_id !== undefined
  // Check if we're currently viewing a trip
  const isViewingTrip = !!tripId
  
  const waypointDay = waypoint?.trip_day ?? 1

  // Determine what data to display - prioritize waypoint over coordinates
  const displayData = useMemo(() => {
    if (waypoint) {
      return {
        name: waypoint.name,
        latitude: waypoint.latitude,
        longitude: waypoint.longitude,
        isWaypoint: true
      }
    } else if (coordinates) {
      return {
        name: locationDisplayName ?? null,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        isWaypoint: false
      }
    }
    return null
  }, [waypoint, coordinates, locationDisplayName])

  // Sync local state with waypoint changes
  useEffect(() => {
    if (!isEditingName && waypoint) {
      setLocalName(waypoint.name || '')
    }
  }, [waypoint?.name, isEditingName])
  
  useEffect(() => {
    setLocalNotes(waypoint?.notes || '')
  }, [waypoint?.notes])
  
  useEffect(() => {
    setCustomLabel(waypoint?.poi_custom_label || '')
  }, [waypoint?.poi_custom_label])

  // Debounced update function for name - only updates after user stops typing for 1500ms
  const debouncedUpdateName = useDebouncedCallback(async (value: string) => {
    if (!waypoint || !onUpdateWaypoint) {
      pendingNameUpdateRef.current = null
      return
    }

    const trimmedValue = value.trim()
    
    // Don't update if value hasn't changed
    if (trimmedValue === waypoint.name) {
      pendingNameUpdateRef.current = null
      return
    }

    try {
      setIsUpdatingName(true)
      await onUpdateWaypoint(waypoint.id, { name: trimmedValue })
      pendingNameUpdateRef.current = null
    } catch (error) {
      // Revert to server value on error
      setLocalName(waypoint.name || '')
      pendingNameUpdateRef.current = null
    } finally {
      setIsUpdatingName(false)
    }
  }, 1500)

  // Immediate update function for blur events
  const updateNameImmediately = async (value: string) => {
    if (!waypoint || !onUpdateWaypoint) {
      setIsEditingName(false)
      return
    }

    // Cancel any pending debounced update
    pendingNameUpdateRef.current = null

    const trimmedValue = value.trim()
    
    // Don't update if value hasn't changed
    if (trimmedValue === waypoint.name) {
      setIsEditingName(false)
      return
    }

    try {
      setIsUpdatingName(true)
      await onUpdateWaypoint(waypoint.id, { name: trimmedValue })
      setIsEditingName(false)
    } catch (error) {
      // Revert to server value on error
      setLocalName(waypoint.name || '')
    } finally {
      setIsUpdatingName(false)
    }
  }

  const handleNameChange = (value: string) => {
    // Update local state immediately for instant UI feedback
    setLocalName(value)
    
    // Store pending update
    pendingNameUpdateRef.current = { value }

    // Debounce the API call
    debouncedUpdateName(value)
  }

  const handleNameBlur = () => {
    // If there's a pending update, execute it immediately
    if (pendingNameUpdateRef.current) {
      updateNameImmediately(pendingNameUpdateRef.current.value)
    } else {
      setIsEditingName(false)
    }
  }

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (pendingNameUpdateRef.current) {
        updateNameImmediately(pendingNameUpdateRef.current.value)
      } else {
        setIsEditingName(false)
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setLocalName(waypoint?.name || '')
      pendingNameUpdateRef.current = null
      setIsEditingName(false)
    }
  }

  // Handle day change
  const handleDayChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!waypoint || !onUpdateWaypointDay) return
    
    const newDay = parseInt(e.target.value, 10)
    if (!newDay || newDay === waypointDay) return

    try {
      setIsUpdatingDay(true)
      await onUpdateWaypointDay(waypoint.id, newDay)
    } catch (error) {
    } finally {
      setIsUpdatingDay(false)
    }
  }

  // Handle type change
  const handleTypeChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!waypoint || !onUpdateWaypoint) return
    
    const newType = e.target.value === '' ? null : (e.target.value as POIType)
    // Compare properly - handle both null and undefined as "no type"
    const currentType = waypoint.poi_type ?? null
    
    if (newType === currentType) return

    try {
      setIsUpdatingType(true)
      const updates: { poi_type?: string | null; poi_custom_label?: string | null } = { poi_type: newType }
      if (newType === 'custom') {
        updates.poi_custom_label = customLabel.trim() || null
      } else {
        updates.poi_custom_label = null
      }
      await onUpdateWaypoint(waypoint.id, updates)
    } catch (error) {
    } finally {
      setIsUpdatingType(false)
    }
  }

  // Debounced update for custom label
  const debouncedUpdateLabel = useDebouncedCallback(async (value: string) => {
    if (!waypoint || !onUpdateWaypoint) {
      pendingLabelUpdateRef.current = null
      return
    }

    const trimmedValue = value.trim()
    const updates = { 
      poi_type: 'custom' as const,
      poi_custom_label: trimmedValue || null 
    }

    try {
      setIsUpdatingType(true)
      await onUpdateWaypoint(waypoint.id, updates)
      pendingLabelUpdateRef.current = null
    } catch (error) {
      setCustomLabel(waypoint.poi_custom_label || '')
      pendingLabelUpdateRef.current = null
    } finally {
      setIsUpdatingType(false)
    }
  }, 1500)

  const updateLabelImmediately = async (value: string) => {
    if (!waypoint || !onUpdateWaypoint) return

    pendingLabelUpdateRef.current = null

    const trimmedValue = value.trim()
    const updates = { 
      poi_type: 'custom' as const,
      poi_custom_label: trimmedValue || null 
    }

    try {
      setIsUpdatingType(true)
      await onUpdateWaypoint(waypoint.id, updates)
    } catch (error) {
      setCustomLabel(waypoint.poi_custom_label || '')
    } finally {
      setIsUpdatingType(false)
    }
  }

  const handleCustomLabelChange = (value: string) => {
    setCustomLabel(value)
    if (!waypoint || !onUpdateWaypoint) return
    pendingLabelUpdateRef.current = { value }
    debouncedUpdateLabel(value)
  }

  const handleCustomLabelBlur = () => {
    if (pendingLabelUpdateRef.current) {
      updateLabelImmediately(pendingLabelUpdateRef.current.value)
    }
  }

  // Debounced update for notes
  const debouncedUpdateNotes = useDebouncedCallback(async (value: string) => {
    if (!waypoint || !onUpdateWaypoint) {
      pendingNotesUpdateRef.current = null
      return
    }

    const trimmedValue = value.trim()
    
    if (trimmedValue === (waypoint.notes || '')) {
      pendingNotesUpdateRef.current = null
      return
    }

    try {
      setIsUpdatingNotes(true)
      await onUpdateWaypoint(waypoint.id, { notes: trimmedValue || undefined })
      pendingNotesUpdateRef.current = null
    } catch (error) {
      setLocalNotes(waypoint.notes || '')
      pendingNotesUpdateRef.current = null
    } finally {
      setIsUpdatingNotes(false)
    }
  }, 1500)

  const updateNotesImmediately = async (value: string) => {
    if (!waypoint || !onUpdateWaypoint) return

    pendingNotesUpdateRef.current = null

    const trimmedValue = value.trim()
    
    if (trimmedValue === (waypoint.notes || '')) {
      return
    }

    try {
      setIsUpdatingNotes(true)
      await onUpdateWaypoint(waypoint.id, { notes: trimmedValue || undefined })
    } catch (error) {
      setLocalNotes(waypoint.notes || '')
    } finally {
      setIsUpdatingNotes(false)
    }
  }

  const handleNotesChange = (value: string) => {
    setLocalNotes(value)
    if (!waypoint || !onUpdateWaypoint) return
    pendingNotesUpdateRef.current = { value }
    debouncedUpdateNotes(value)
  }

  const handleNotesBlur = () => {
    if (pendingNotesUpdateRef.current) {
      updateNotesImmediately(pendingNotesUpdateRef.current.value)
    }
  }

  if (!displayData) return null

  const showAddStopButton = !displayData.isWaypoint && tripId && onAddStop && !isLibraryView
  const showAddToLibraryButton = isLibraryView && !displayData.isWaypoint && coordinates && onAddToLibrary

  const inner = (
    <>
      <div
        className={cn(
          'flex items-center justify-between p-4 border-b shrink-0',
          isFloating ? 'border-earth-200' : 'border-gray-200 pointer-events-auto'
        )}
        onClick={!isFloating ? (e: React.MouseEvent) => e.stopPropagation() : undefined}
      >
        <div className="flex items-center gap-2">
          <MapPin className={cn('w-5 h-5', isFloating ? 'text-adventure-600' : 'text-primary')} />
          <h2 className={cn('text-lg font-semibold', isFloating ? 'text-foreground' : 'text-gray-900')}>
            {displayData.isWaypoint ? 'Waypoint Details' : 'Location'}
          </h2>
        </div>
        {isFloating ? (
          <Button
            variant="ghost"
            size="icon"
            className={FLOATING_CLOSE_BUTTON_CLASS}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onClose()
            }}
            aria-label="Close panel"
            type="button"
          >
            <X className="h-5 w-5" />
          </Button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onClose()
            }}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors pointer-events-auto relative z-[9999]"
            aria-label="Close panel"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div
        className={cn(
          'flex-1 overflow-y-auto p-4 pointer-events-auto',
          isFloating ? 'min-h-0' : undefined
        )}
      >
        {/* Waypoint Name */}
        {displayData.isWaypoint && waypoint && (
          <div className="mb-6">
            {isEditingName ? (
              <div className="space-y-2">
                <Input
                  value={localName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onBlur={handleNameBlur}
                  onKeyDown={handleNameKeyDown}
                  placeholder="Enter waypoint name"
                  maxLength={255}
                  disabled={isUpdatingName}
                  className="text-base font-medium"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">Press Enter to save, Esc to cancel</p>
              </div>
            ) : (
              <h3 
                className={cn(
                  'text-base font-medium mb-2',
                  isFloating ? 'text-foreground' : 'text-gray-900',
                  onUpdateWaypoint
                    ? 'cursor-pointer hover:text-primary transition-colors px-2 py-1 rounded border border-transparent hover:border-earth-200 dark:hover:border-earth-600 inline-block'
                    : ''
                )}
                onClick={onUpdateWaypoint ? () => setIsEditingName(true) : undefined}
                title={onUpdateWaypoint ? 'Click to edit name' : undefined}
              >
                {displayData.name || 'Unnamed waypoint'}
              </h3>
            )}
          </div>
        )}
        {!displayData.isWaypoint && displayData.name && (
          <div className="mb-6">
            <h3
              className={cn(
                'text-base font-medium mb-2',
                isFloating ? 'text-earth-900 dark:text-foreground' : 'text-gray-900'
              )}
            >
              {displayData.name}
            </h3>
          </div>
        )}

        {/* Coordinates Section */}
        <div className="space-y-4">
          <div>
            <h4
              className={cn(
                'text-sm font-medium mb-2',
                isFloating ? 'text-earth-800 dark:text-earth-200' : 'text-gray-700'
              )}
            >
              Coordinates
            </h4>
            {displayData.latitude != null && displayData.longitude != null ? (
              <button
                type="button"
                onClick={async () => {
                  const success = await copyCoordinatesToClipboard(displayData.latitude, displayData.longitude)
                  if (success) {
                    setCopiedCoordinates(true)
                    setTimeout(() => setCopiedCoordinates(false), 2000)
                  }
                }}
                className={cn(
                  'w-full rounded-lg p-3 text-left transition-colors group',
                  isFloating
                    ? 'bg-earth-100 dark:bg-earth-900/40 hover:bg-earth-200 dark:hover:bg-earth-800/50'
                    : 'bg-gray-50 hover:bg-gray-100'
                )}
                title="Click to copy coordinates"
              >
                <div
                  className={cn(
                    'text-sm flex items-center gap-1.5',
                    isFloating ? 'text-earth-800 dark:text-earth-200' : 'text-gray-600'
                  )}
                >
                  <span>{formatCoordinatesForCopy(displayData.latitude, displayData.longitude)}</span>
                  {copiedCoordinates ? (
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                  ) : (
                    <Copy className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  )}
                </div>
              </button>
            ) : (
              <div
                className={cn(
                  'rounded-lg p-3',
                  isFloating ? 'bg-earth-100 dark:bg-earth-900/40' : 'bg-gray-50'
                )}
              >
                <div className={cn('text-sm', isFloating ? 'text-muted-foreground' : 'text-gray-500')}>
                  Coordinates not available
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Waypoint-specific fields */}
        {displayData.isWaypoint && waypoint && (
          <div className="space-y-4 mt-6">
            {/* Day Slot */}
            {totalDays && totalDays > 1 && onUpdateWaypointDay && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Day
                </label>
                <select
                  value={waypointDay}
                  onChange={handleDayChange}
                  disabled={isUpdatingDay}
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {Array.from({ length: totalDays }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>
                      Day {day}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Label (POI Type) */}
            {onUpdateWaypoint && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Label
                </label>
                <select
                  value={waypoint.poi_type || ''}
                  onChange={handleTypeChange}
                  disabled={isUpdatingType}
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {POI_TYPE_OPTIONS.map((option) => (
                    <option key={option.value || 'none'} value={option.value || ''}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Custom Label */}
            {waypoint.poi_type === 'custom' && onUpdateWaypoint && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2">Custom Label</label>
                <Input
                  value={customLabel}
                  onChange={(e) => handleCustomLabelChange(e.target.value)}
                  onBlur={handleCustomLabelBlur}
                  placeholder="Enter custom label"
                  maxLength={50}
                  disabled={isUpdatingType}
                  className="text-sm"
                />
              </div>
            )}

            {/* Notes */}
            {onUpdateWaypoint && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Notes
                </label>
                <textarea
                  value={localNotes}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  onBlur={handleNotesBlur}
                  placeholder="Add notes about this waypoint..."
                  disabled={isUpdatingNotes}
                  rows={4}
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed resize-y"
                />
              </div>
            )}

            {/* Add to Trip - Show for library waypoints only when NOT viewing a trip */}
            {isLibraryWaypoint && !isViewingTrip && (
              <div>
                <button
                  onClick={() => setShowAddToTripPanel(true)}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add to Trip
                </button>
              </div>
            )}

            {/* Add to Library - Show for trip-specific waypoints, or when viewing a trip */}
            {(isTripSpecificWaypoint || (isLibraryWaypoint && isViewingTrip)) && (
              <div>
                <button
                  onClick={() => setShowAddToLibraryDialog(true)}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <FolderPlus className="w-4 h-4" />
                  Add to Library
                </button>
              </div>
            )}
          </div>
        )}

        {/* Add Stop Button */}
        {showAddStopButton && (
          <div className="mt-6">
            <button
              onClick={onAddStop}
              disabled={isAddingStop}
              className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
            >
              <MapPin className="w-4 h-4" />
              {isAddingStop ? 'Adding...' : 'Add Stop'}
            </button>
          </div>
        )}

        {/* Add to Library Button - Show when in library view and clicking on map */}
        {showAddToLibraryButton && (
          <div className="mt-6">
            <button
              onClick={async () => {
                if (!onAddToLibrary) return
                setIsAddingToLibrary(true)
                try {
                  await onAddToLibrary()
                } finally {
                  setIsAddingToLibrary(false)
                }
              }}
              disabled={isAddingToLibrary}
              className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
            >
              <FolderPlus className="w-4 h-4" />
              {isAddingToLibrary ? 'Adding...' : 'Add to Library'}
            </button>
          </div>
        )}
      </div>

      {/* Add to Library Dialog */}
      {waypoint && (
        <AddWaypointToLibraryDialog
          waypoint={waypoint}
          open={showAddToLibraryDialog}
          onOpenChange={setShowAddToLibraryDialog}
          onSuccess={() => {
            // Waypoint will be refreshed by parent component
          }}
        />
      )}

      {/* Add to Trip Panel */}
      {waypoint && showAddToTripPanel && (
        <AddWaypointToTripPanel
          waypoint={waypoint}
          onClose={() => setShowAddToTripPanel(false)}
          onSuccess={() => {
            setShowAddToTripPanel(false)
            // Waypoint will be refreshed by parent component
          }}
        />
      )}
    </>
  )

  if (isFloating) {
    return (
      <Sheet defaultOpen onOpenChange={(open) => { if (!open) onClose() }}>
        <SheetContent
          side="bottom"
          className={cn(
            'p-0 gap-0 max-h-[90dvh] min-h-0 flex flex-col items-stretch border-0 bg-transparent shadow-none',
            'md:items-center md:px-4',
            className
          )}
        >
          {/*
            Narrow card is nested so SheetContent stays full-width for slide-in-from-bottom
            (centering translate on the animated node fights the bottom slide transform).
          */}
          <div className="flex w-full max-h-[90dvh] min-h-0 flex-col overflow-hidden rounded-t-xl border border-earth-200 border-b-0 bg-earth-50 shadow-xl dark:bg-earth-100 md:max-w-sm">
            <div className="flex shrink-0 justify-center pt-2 pb-1" aria-hidden>
              <div className="h-1 w-10 rounded-full bg-earth-300 dark:bg-earth-600" />
            </div>
            <SheetBody className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-0">
              <div className="flex min-h-0 flex-1 flex-col">{inner}</div>
            </SheetBody>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <div
      className={cn(
        'fixed right-0 top-20 h-[calc(100vh-5rem)] w-64 bg-white border-l border-gray-200 shadow-lg z-[9999] flex flex-col transition-transform duration-300 ease-in-out pointer-events-auto',
        className
      )}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {inner}
    </div>
  )
}
